import React from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '@features/auth/hooks/useAuth';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import client from '@apollolocal/client';
import MuiThemeProvider from '@muilocal/MuiThemeProvider';
import { APIProvider } from '@vis.gl/react-google-maps';
import DealerAppFrame from '@features/layout/DealerAppFrame';
import DealerUpdater from '@features/appUpdater/DealerUpdater';
import AppRoutes from './Routes';
import { ErrorBoundary } from 'react-error-boundary';
import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import UserAgreementModal from '@features/auth/components/UserAgreementModal';
import AccountLoadFailureScreen from '@features/auth/components/AccountLoadFailureScreen';
import { getUserAgreement, UserAgreementContent } from '@lib/contentful';
import { useUser, setUser } from '@store';
import { getUserProfile } from '@features/auth/services/authService';
import termsService from '@features/auth/services/termsService';
import { toast } from 'react-toastify';
const { VITE_GOOGLE_API_KEY } = import.meta.env;

const App: React.FC = () => {
  const { loading, accountFailure, retryAccount, signOutFromAccountFailure } = useAuth();
  const user = useUser();
  const [userAgreementContent, setUserAgreementContent] = React.useState<UserAgreementContent | null>(null);
  const [isLoadingAgreement, setIsLoadingAgreement] = React.useState(false);
  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [isAcceptingTerms, setIsAcceptingTerms] = React.useState(false);
  const [acceptError, setAcceptError] = React.useState<string | null>(null);

  // Check if user needs to accept terms and fetch agreement immediately
  React.useEffect(() => {
    if (user?.profile && !user.profile.accepted_terms && !showTermsModal && !isLoadingAgreement) {
      // Show modal immediately (before content loads)
      setShowTermsModal(true);
      setIsLoadingAgreement(true);

      getUserAgreement()
        .then((content) => {
          setUserAgreementContent(content);
        })
        .catch((error) => {
          console.error('Failed to fetch user agreement:', error);

          // Log to Sentry
          Sentry.captureException(error, {
            tags: {
              feature: 'terms-acceptance',
              action: 'fetch-agreement-content'
            },
            extra: {
              userId: user?.profile?.id,
              userEmail: user?.profile?.email
            },
          });

          toast.error(
            'Failed to load User Agreement from server. Please contact HopDrive support.',
            { autoClose: false }
          );
        })
        .finally(() => {
          setIsLoadingAgreement(false);
        });
    }

    // Close modal if user has accepted terms
    if (user?.profile?.accepted_terms && showTermsModal) {
      setShowTermsModal(false);
    }
  }, [user?.profile?.id, user?.profile?.accepted_terms]);

  const handleAcceptTerms = async () => {
    if (!user?.profile?.id || !user?.profile?.email || !userAgreementContent) {
      const errorMsg = 'User information not available. Please refresh the page and try again.';
      setAcceptError(errorMsg);

      // Log validation error to Sentry
      Sentry.captureMessage(errorMsg, {
        level: 'warning',
        tags: {
          feature: 'terms-acceptance',
          action: 'accept-terms-validation'
        },
        extra: {
          hasUserId: !!user?.profile?.id,
          hasUserEmail: !!user?.profile?.email,
          hasAgreementContent: !!userAgreementContent,
          userId: user?.profile?.id,
          userEmail: user?.profile?.email
        },
      });

      return;
    }

    setIsAcceptingTerms(true);
    setAcceptError(null); // Clear any previous errors

    try {
      await termsService.acceptTerms(
        user.profile.id,
        user.profile.email,
        userAgreementContent.publishedVersion,
        userAgreementContent.publishedAt
      );

      // Refresh user profile to get updated accepted_terms value
      const updatedProfile = await getUserProfile(user.profile.id);

      if (updatedProfile) {
        setUser({
          ...user,
          profile: updatedProfile,
        });
      } else {
        console.error('Failed to get updated profile');

        // Log profile refresh failure
        Sentry.captureMessage('Failed to refresh user profile after accepting terms', {
          level: 'warning',
          tags: {
            feature: 'terms-acceptance',
            action: 'refresh-profile'
          },
          extra: {
            userId: user.profile.id,
            userEmail: user.profile.email
          },
        });
      }

      setShowTermsModal(false);
      toast.success('User Agreement accepted successfully');
    } catch (err) {
      console.error('Error accepting terms:', err);

      // Note: The error is already captured in termsService.ts with full context
      // We just set the UI error state here

      setAcceptError('Failed to accept User Agreement. Please check your connection and try again. If the problem persists, contact HopDrive support.');
      toast.error('Failed to accept User Agreement. Please try again.');
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={DefaultErrorFallback}
      onError={(error, info) => {
        // Log the error to your error reporting service here
        console.error('Uncaught error:', error, info);
      }}
    >
      <MuiThemeProvider>
        <DealerUpdater>
          <BrowserRouter>
            <ApolloProvider client={client}>
              <APIProvider apiKey={VITE_GOOGLE_API_KEY}>
                {accountFailure ? (
                  // Signed in, but the account did not load: nothing else renders until Retry works.
                  <AccountLoadFailureScreen retrying={accountFailure.retrying} onRetry={retryAccount} onSignOut={signOutFromAccountFailure} />
                ) : (
                  <DealerAppFrame>{!loading && <AppRoutes />}</DealerAppFrame>
                )}

                {/* Global User Agreement Modal - appears at app level before any routes */}
                {showTermsModal && (
                  <UserAgreementModal
                    open={true}
                    onAccept={handleAcceptTerms}
                    loading={isAcceptingTerms}
                    content={userAgreementContent?.content}
                    version={userAgreementContent?.publishedVersion}
                    isLoadingContent={isLoadingAgreement}
                    error={acceptError}
                  />
                )}
              </APIProvider>
            </ApolloProvider>
          </BrowserRouter>
        </DealerUpdater>
      </MuiThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
